// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Analytics Pages
// ItemSummaryPage, ItemDetailPage, InventoryDetailPage, OfferPerformancePage

import { useEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeToDoc, subscribeToQuery, callFunction } from '../../services/firebase';

// ─────────────────────────────────────────────────────────────────────────────
// ITEM SUMMARY PAGE
// ─────────────────────────────────────────────────────────────────────────────

export function ItemSummaryPage() {
  const { activeOrgId, activeOrg } = useAuth();
  const navigate = useNavigate();
  const [rollup, setRollup] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSlice, setActiveSlice] = useState('all');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToDoc<Record<string, unknown>>(
      `org_list_rollups/${activeOrgId}/snapshots/${today}`,
      data => { setRollup(data); setIsLoading(false); }
    );
  }, [activeOrgId, today]);

  const slicers = ['all', 'user-assigned', 'shared', 'by-zip', 'by-type'];

  const counts = rollup?.itemCounts as Record<string, Record<string, unknown>> | undefined;
  const listCounts = rollup?.listCounts as Record<string, number> | undefined;

  const metrics = counts ? [
    { label: 'Items: All, Open',
      value: (counts.all?.open as number)?.toLocaleString() ?? '—', green: true, indent: true },
    { label: 'Items: All, Closed Today',
      value: (counts.all?.closedToday as number)?.toLocaleString() ?? '—', indent: true },
    { label: 'Items: All, Av. Buy Time',
      value: counts.all?.avBuyTimeDays ? `${counts.all.avBuyTimeDays}d` : '—', indent: true },
    { label: 'Items: All, Av. Unit Price',
      value: counts.all?.avUnitPrice
        ? `$${(counts.all.avUnitPrice as number).toFixed(2)}` : '—', indent: true },
    { label: 'Items: All, Total Rev (1d)',
      value: counts.all?.totalRevDay
        ? `$${(counts.all.totalRevDay as number).toLocaleString()}` : '—',
      green: true, indent: true },
  ] : [];

  return (
    <div>
      <div className="breadcrumb">
        <span>Reports</span>
        <span>›</span>
        <span style={{ color: 'var(--color-text)' }}>Item Summary</span>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Reports › Item Summary</span>
          <span className="badge badge-gray">{activeOrg?.name}</span>
        </div>

        <div className="slicer-row">
          {slicers.map(s => (
            <button key={s}
              className={`slicer-btn${activeSlice === s ? ' active' : ''}`}
              onClick={() => setActiveSlice(s)}>
              {s === 'all' ? 'Lists: All'
                : s === 'user-assigned' ? 'User-Assigned'
                : s === 'shared' ? 'Shared'
                : s === 'by-zip' ? 'By Zip ▼'
                : 'By Type ▼'}
            </button>
          ))}
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/analytics/items/detail')}>
            View Detail →
          </button>
        </div>

        {isLoading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : !rollup ? (
          <div className="empty-state">
            No rollup data yet. Data updates daily at midnight ET.
          </div>
        ) : (
          <>
            <div className="metric-group">
              <span className="group-label">
                Lists: All &nbsp; {listCounts?.all?.toLocaleString() ?? '—'} lists
              </span>
              {metrics.map(m => (
                <div key={m.label}
                  className={`metric-row${m.indent ? ' metric-indent' : ''}`}>
                  <span className="metric-label">{m.label}</span>
                  <span className={`metric-val${m.green ? ' green' : ''}`}>{m.value}</span>
                </div>
              ))}
            </div>
            <div className="metric-group">
              <span className="group-label">
                Lists: User-Assigned &nbsp;
                {listCounts?.userAssigned?.toLocaleString() ?? '—'} lists
              </span>
              <div className="metric-row metric-indent">
                <span className="metric-label">Items: User-Assigned</span>
                <span className="metric-val">
                  {(counts?.userAssigned?.open as number)?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
            <div className="metric-group">
              <span className="group-label">
                Lists: Shared &nbsp; {listCounts?.shared?.toLocaleString() ?? '—'} lists
              </span>
              <div className="metric-row metric-indent">
                <span className="metric-label">Items: Shared</span>
                <span className="metric-val blue">
                  {(counts?.shared?.open as number)?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
            <div className="note-box">
              <strong>Shared lists</strong> are visible to multiple orgs — a competitive
              bid situation. User consent required for all data shown.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEM DETAIL PAGE
// ─────────────────────────────────────────────────────────────────────────────

interface DetailItem {
  id: string;
  desc: string;
  brand?: string;
  type: string;
  userCount: number;
  avgUnitPrice?: number;
  hasActiveOffer: boolean;
}

export function ItemDetailPage() {
  const { activeOrgId, activeOrg } = useAuth();
  const [items, setItems] = useState<DetailItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [slicer, setSlicer] = useState('all');
  const [offerStates, setOfferStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToQuery<DetailItem>(
      `org_list_rollups/${activeOrgId}/itemDetail`,
      [],
      data => {
        const sorted = data.sort((a, b) => b.userCount - a.userCount);
        setItems(sorted);
        const states: Record<string, boolean> = {};
        sorted.forEach(d => { states[d.id] = d.hasActiveOffer; });
        setOfferStates(states);
        setIsLoading(false);
      }
    );
  }, [activeOrgId]);

  const filtered = items.filter(item => {
    const matchSearch = !search ||
      `${item.desc} ${item.brand ?? ''} ${item.type}`
        .toLowerCase().includes(search.toLowerCase());
    const matchSlicer = slicer === 'all'
      || (slicer === 'offered'     &&  item.hasActiveOffer)
      || (slicer === 'not-offered' && !item.hasActiveOffer);
    return matchSearch && matchSlicer;
  });

  const maxUsers = items[0]?.userCount || 1;

  const toggleOffer = (itemId: string) => {
    setOfferStates(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const activeOfferCount = Object.values(offerStates).filter(Boolean).length;

  return (
    <div>
      <div className="breadcrumb">
        <a href="/analytics/items">Reports › Item Summary</a>
        <span>›</span>
        <span style={{ color: 'var(--color-text)' }}>Item Detail</span>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title"
            style={{ color: '#fff', background: 'var(--color-primary)',
              padding: '5px 12px', borderRadius: 'var(--radius-md)' }}>
            Requested Items ▼
          </span>
          <span className="badge badge-gray">{activeOrg?.name}</span>
        </div>

        <div className="slicer-row" style={{ alignItems: 'center' }}>
          <input type="search" placeholder="Search item, brand, type…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            style={{ background: 'var(--color-bg-secondary)',
              border: '0.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', padding: '4px 10px',
              fontSize: 12, color: 'var(--color-text)', outline: 'none', width: 180 }}
            aria-label="Search items" />
          {['all','offered','not-offered','by-type'].map(s => (
            <button key={s}
              className={`slicer-btn${slicer === s ? ' active' : ''}`}
              onClick={() => setSlicer(s)}>
              {s === 'all' ? 'All'
                : s === 'offered' ? 'Offered'
                : s === 'not-offered' ? 'Not Offered'
                : 'By Type ▼'}
            </button>
          ))}
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }}>
            + Make Offer
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {isLoading ? (
            <div className="empty-state"><span className="spinner" /></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>Offer</th>
                  <th>Type</th><th>Description</th><th>URL</th>
                  <th>Brand</th><th>Offer Type</th>
                  <th>Current Price</th><th>Offer Price</th>
                  <th className="sorted">Users</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id}
                    className={offerStates[item.id] ? 'has-offer' : ''}>
                    <td>
                      <button
                        className={`offer-dot${offerStates[item.id] ? ' active' : ''}`}
                        onClick={() => toggleOffer(item.id)}
                        title={offerStates[item.id] ? 'Remove offer' : 'Create offer'}
                        aria-label={offerStates[item.id]
                          ? `Remove offer: ${item.desc}`
                          : `Create offer: ${item.desc}`}
                      />
                    </td>
                    <td style={{ fontSize: 12 }}>{item.type}</td>
                    <td style={{ fontSize: 12, fontWeight: 500 }}>{item.desc}</td>
                    <td>
                      <input type="url" className="url-input" placeholder="url…"
                        aria-label={`URL for ${item.desc}`} />
                    </td>
                    <td style={{ fontSize: 12 }}>{item.brand ?? '—'}</td>
                    <td>
                      <select className="offer-select" aria-label="Offer type">
                        <option>BOGO</option><option>Discount</option>
                        <option>% Off</option><option>3 for 2</option>
                      </select>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {item.avgUnitPrice ? `$${item.avgUnitPrice.toFixed(2)}` : '—'}
                    </td>
                    <td>
                      <input type="text"
                        className={`price-input${offerStates[item.id] ? ' offer' : ''}`}
                        placeholder="$0.00"
                        defaultValue={item.avgUnitPrice
                          ? `$${(item.avgUnitPrice * 0.85).toFixed(2)}` : ''}
                        aria-label={`Offer price for ${item.desc}`} />
                    </td>
                    <td>
                      <div className="users-bar">
                        <div className="users-bar-wrap">
                          <div className="users-bar-fill"
                            style={{ width: `${(item.userCount / maxUsers) * 100}%` }} />
                        </div>
                        <span className="users-count">
                          {item.userCount.toLocaleString()}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px',
          background: 'var(--color-bg-secondary)',
          borderTop: '0.5px solid var(--color-border)',
          fontSize: 12, color: 'var(--color-text-secondary)', gap: 10 }}>
          <span style={{ flex: 1 }}>
            {activeOfferCount} items with active offers ·{' '}
            {items.length - activeOfferCount} without · sorted by user demand
          </span>
          <button className="btn btn-primary btn-sm">Create Offers for Selected</button>
        </div>

        <div className="note-box">
          Each row is a unique <strong>Description + Brand</strong> pair. Tap the offer
          dot to create or remove an offer. Green rows have active offers.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INVENTORY DETAIL PAGE (V2)
// ─────────────────────────────────────────────────────────────────────────────

interface InventoryItem {
  id: string;
  type?: string;
  desc: string;
  brand?: string;
  qty?: string;
  unitCost?: number;
  stockQty?: number;
  hasActiveOffer?: boolean;
  isRequested?: boolean;
}

export function InventoryDetailPage() {
  const { activeOrgId, activeOrg } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [slicer, setSlicer] = useState('all');

  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToQuery<InventoryItem>(
      `org_inventory/${activeOrgId}/items`,
      [],
      data => { setItems(data); setIsLoading(false); }
    );
  }, [activeOrgId]);

  const maxStock = Math.max(...items.map(i => i.stockQty ?? 0), 1);

  const getStockColor = (qty: number) => {
    const pct = qty / maxStock;
    if (pct < 0.1) return '#e53935';
    if (pct < 0.3) return '#f9a825';
    return '#2e7d32';
  };

  const filtered = items.filter(item => {
    const matchSearch = !search ||
      `${item.desc} ${item.brand ?? ''} ${item.type ?? ''}`
        .toLowerCase().includes(search.toLowerCase());
    const matchSlicer = slicer === 'all'
      || (slicer === 'offered'     &&  item.hasActiveOffer)
      || (slicer === 'not-offered' && !item.hasActiveOffer)
      || (slicer === 'requested'   &&  item.isRequested)
      || (slicer === 'low-stock'   && (item.stockQty ?? 0) / maxStock < 0.1);
    return matchSearch && matchSlicer;
  });

  return (
    <div>
      <div className="breadcrumb">
        <a href="/analytics">Reports</a>
        <span>›</span>
        <span style={{ color: 'var(--color-text)' }}>Inventory Detail</span>
      </div>

      <div className="note-box warning" style={{ marginBottom: 16 }}>
        <strong>V2 Feature.</strong> Upload a CSV or connect via API in
        Settings → Integrations. Cost and stock figures are private —
        never shared with users or other orgs.
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title"
            style={{ color: '#fff', background: 'var(--color-primary)',
              padding: '5px 12px', borderRadius: 'var(--radius-md)' }}>
            Inventory Items ▼
          </span>
          <span className="badge badge-gray">{activeOrg?.name}</span>
        </div>

        <div className="slicer-row" style={{ alignItems: 'center' }}>
          <input type="search" placeholder="Search item, brand, type…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            style={{ background: 'var(--color-bg-secondary)',
              border: '0.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', padding: '4px 10px',
              fontSize: 12, color: 'var(--color-text)', outline: 'none', width: 180 }}
            aria-label="Search inventory" />
          {['all','offered','not-offered','requested','low-stock'].map(s => (
            <button key={s}
              className={`slicer-btn${slicer === s ? ' active' : ''}`}
              onClick={() => setSlicer(s)}>
              {s === 'all' ? 'All'
                : s === 'offered' ? 'Offered'
                : s === 'not-offered' ? 'Not Offered'
                : s === 'requested' ? 'Requested'
                : 'Low Stock'}
            </button>
          ))}
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }}>
            ↑ Upload CSV
          </button>
          <button className="btn btn-primary btn-sm">+ Make Offer</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {isLoading ? (
            <div className="empty-state"><span className="spinner" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              No inventory yet. Upload a CSV to get started.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>Offer</th>
                  <th>Type</th><th>Description</th><th>URL</th>
                  <th>Brand</th><th>Qty</th><th>Offer Type</th>
                  <th>Cost ⚿</th>
                  <th className="sorted">Stock</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const stockPct = (item.stockQty ?? 0) / maxStock;
                  const isLow = stockPct < 0.1;
                  return (
                    <tr key={item.id}
                      style={isLow
                        ? { background: 'var(--color-warning-bg)' } : {}}>
                      <td>
                        <button className="offer-dot"
                          aria-label={`Create offer: ${item.desc}`} />
                      </td>
                      <td style={{ fontSize: 12 }}>{item.type ?? '—'}</td>
                      <td style={{ fontSize: 12, fontWeight: 500 }}>{item.desc}</td>
                      <td>
                        <input type="url" className="url-input" placeholder="url…"
                          aria-label={`URL for ${item.desc}`} />
                      </td>
                      <td style={{ fontSize: 12 }}>{item.brand ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{item.qty ?? '—'}</td>
                      <td>
                        <select className="offer-select" aria-label="Offer type">
                          <option>Discount</option><option>BOGO</option>
                          <option>% Off</option>
                        </select>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {item.unitCost ? `$${item.unitCost.toFixed(2)}` : '—'}
                      </td>
                      <td>
                        <div className="stock-bar-wrap">
                          <div className="stock-bar">
                            <div className="stock-bar-fill"
                              style={{ width: `${stockPct * 100}%`,
                                background: getStockColor(item.stockQty ?? 0) }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600,
                            color: isLow
                              ? 'var(--color-danger)' : 'var(--color-text)' }}>
                            {(item.stockQty ?? 0).toLocaleString()}
                            {isLow ? ' ⚠' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="note-box private" style={{ margin: '10px 14px' }}>
          🔒 <strong>Cost and stock figures are private to {activeOrg?.name}.</strong>{' '}
          Never included in user rollups, offer data, or shared with any other org.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OFFER PERFORMANCE PAGE
// ─────────────────────────────────────────────────────────────────────────────

interface OfferPerf {
  all?: {
    offeredItems?: number; offeredQty?: number; uniqueOrgsReached?: number;
    avgOrgTakeRate?: number; avgOfferTakeRate?: number;
    avgUnitPrice?: number; avgUnitCost?: number; avgMargin?: number; totalRevDay?: number;
  };
  requested?: { offerCount?: number };
  unrequested?: { offerCount?: number };
}

export function OfferPerformancePage() {
  const { activeOrgId, activeOrg } = useAuth();
  const [perf, setPerf] = useState<OfferPerf | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'requested' | 'unrequested'>('all');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToDoc<OfferPerf>(
      `offer_performance_rollups/${activeOrgId}/daily/${today}`,
      data => { setPerf(data); setIsLoading(false); }
    );
  }, [activeOrgId, today]);

  const data = perf?.all ?? {};
  const fmtPct   = (v?: number) => v != null ? `${Math.round(v * 100)}%` : '—';
  const fmtMoney = (v?: number) => v != null ? `$${v.toLocaleString()}` : '—';
  const fmtNum   = (v?: number) => v != null ? v.toLocaleString() : '—';

  const metrics = [
    { label: 'Offered Items',       value: fmtNum(data.offeredItems) },
    { label: 'Offered Qty',         value: fmtNum(data.offeredQty) },
    { label: 'Unique Orgs Reached', value: fmtNum(data.uniqueOrgsReached), blue: true },
    { label: 'Av. Org Take Rate',   value: fmtPct(data.avgOrgTakeRate),   green: true },
    { label: 'Av. Offer Take Rate', value: fmtPct(data.avgOfferTakeRate), green: true },
    { label: 'Av. Unit Price',      value: fmtMoney(data.avgUnitPrice) },
    { label: 'Av. Unit Cost',       value: fmtMoney(data.avgUnitCost) },
    { label: 'Av. Margin',          value: fmtPct(data.avgMargin),        green: true },
    { label: 'Total Rev (1d)',       value: fmtMoney(data.totalRevDay),    green: true },
  ];

  const reqCount   = perf?.requested?.offerCount   ?? 0;
  const unreqCount = perf?.unrequested?.offerCount ?? 0;
  const total = reqCount + unreqCount || 1;

  return (
    <div>
      <div className="breadcrumb">
        <span>Reports</span><span>›</span>
        <span style={{ color: 'var(--color-text)' }}>Offer Performance</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* All offers panel */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Offers: All Items</span>
            <span className="badge badge-gray">{activeOrg?.name}</span>
          </div>

          <div className="tab-row" style={{ marginBottom: 0 }}>
            {(['all','requested','unrequested'] as const).map(t => (
              <button key={t}
                className={`tab${activeTab === t ? ' active' : ''}`}
                onClick={() => setActiveTab(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          <div className="slicer-row">
            {['All','Requested','Unrequested','By Type ▼','By Zip ▼'].map(s => (
              <button key={s} className="slicer-btn">{s}</button>
            ))}
          </div>

          {isLoading ? (
            <div className="empty-state"><span className="spinner" /></div>
          ) : (
            <>
              <div className="metric-group">
                <span className="group-label">
                  Offers: {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                </span>
                {metrics.map(m => (
                  <div key={m.label} className="metric-row metric-indent">
                    <span className="metric-label">{m.label}</span>
                    <span className={`metric-val${m.green ? ' green' : m.blue ? ' blue' : ''}`}>
                      {m.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="metric-group">
                <span className="group-label blue">Offers: Requested, All</span>
                <div className="metric-row metric-indent">
                  <span className="metric-label muted">Demand-signal driven</span>
                  <span className="metric-val" style={{ color: 'var(--color-info)' }}>
                    {reqCount.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="metric-group">
                <span className="group-label amber">Offers: Unrequested, All</span>
                <div className="metric-row metric-indent">
                  <span className="metric-label muted">Proactive — no list signal</span>
                  <span className="metric-val amber">{unreqCount.toLocaleString()}</span>
                </div>
              </div>

              <div className="compare-bar-outer">
                <div className="compare-header">
                  Requested vs Unrequested — offer volume
                </div>
                <div className="compare-row">
                  <span className="compare-label"
                    style={{ color: 'var(--color-info)' }}>Requested</span>
                  <div className="compare-bar-wrap">
                    <div className="compare-fill"
                      style={{ width: `${(reqCount / total) * 100}%`,
                        background: 'var(--color-info)' }} />
                  </div>
                  <span className="compare-val" style={{ color: 'var(--color-info)' }}>
                    {reqCount.toLocaleString()}
                  </span>
                </div>
                <div className="compare-row">
                  <span className="compare-label"
                    style={{ color: 'var(--color-warning)' }}>Unrequested</span>
                  <div className="compare-bar-wrap">
                    <div className="compare-fill"
                      style={{ width: `${(unreqCount / total) * 100}%`,
                        background: '#f9a825' }} />
                  </div>
                  <span className="compare-val" style={{ color: 'var(--color-warning)' }}>
                    {unreqCount.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="note-box">
                <strong>Unique Orgs</strong> counts household groups reached — not
                individual users. Individual identities are never shown.
              </div>
            </>
          )}
        </div>

        {/* Unrequested detail panel */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Offers: Unrequested Items</span>
            <span className="badge badge-gray">{activeOrg?.name}</span>
          </div>
          <div className="slicer-row">
            {['All','Offered','Not Offered','By Type ▼','By Zip ▼'].map(s => (
              <button key={s} className="slicer-btn">{s}</button>
            ))}
          </div>
          <div className="metric-group">
            <span className="group-label amber">Offers: Unrequested</span>
            {metrics.map(m => (
              <div key={m.label} className="metric-row metric-indent">
                <span className="metric-label">{m.label}</span>
                <span className={`metric-val${m.green ? ' green' : ''}`}>{m.value}</span>
              </div>
            ))}
          </div>
          <div className="note-box">
            Compare take rates against Requested offers to evaluate whether proactive
            offers generate meaningful ROI vs demand-driven offers.
          </div>
        </div>
      </div>
    </div>
  );
}
